/**
 * Bounds utilities for map-based search
 *
 * Snapping bounds to a grid improves cache hit rate by normalizing
 * similar viewport positions to the same query key.
 */
import { snapMapBounds } from '@letsgethome/lib/wasm';
import { LISTINGS_BOUNDS_PARAM_NAMES, type ListingsBoundsParams } from '@letsgethome/routes';

export interface MapBounds {
  north: number;
  east: number;
  south: number;
  west: number;
}

/**
 * Snap bounds to a grid for cache-efficient query keys.
 *
 * Without snapping, each pixel-movement creates a unique key with 0% cache hits.
 * With snapping, small movements stay in the same grid cell → cache hits.
 *
 * The snap itself (outward expansion on the shared ~1km grid) is the Rust geo
 * SSOT's (`snapMapBounds`) — the same compiled algorithm every platform's map
 * cache keys on; this wrapper only adapts it to the local `MapBounds` shape.
 */
export function snapBoundsToGrid(bounds: MapBounds): MapBounds {
  const snapped = snapMapBounds(bounds.north, bounds.south, bounds.east, bounds.west);
  return {
    north: snapped.north,
    east: snapped.east,
    south: snapped.south,
    west: snapped.west,
  };
}

/**
 * Compare two bounds for equality (for debounce short-circuit)
 */
export function boundsEqual(a: MapBounds | undefined, b: MapBounds | undefined): boolean {
  if (!a || !b) return a === b;
  return a.north === b.north && a.east === b.east && a.south === b.south && a.west === b.west;
}

/**
 * Format bounds for URL params (6 decimal places) — the `bounds` half of a
 * listings filter MUTATION (`applyListingsFilterMutation`), whose codec owns the
 * four param names.
 */
export function formatBoundsForUrl(bounds: MapBounds): ListingsBoundsParams {
  return {
    north: bounds.north.toFixed(6),
    south: bounds.south.toFixed(6),
    east: bounds.east.toFixed(6),
    west: bounds.west.toFixed(6),
  };
}

/**
 * Parse map bounds from URL search params — the inverse of `formatBoundsForUrl`.
 * Returns `undefined` unless ALL four edges are present and finite; a partial or
 * malformed set is treated as "no viewport filter", so the SSR loader and the
 * client agree: a `?north=…&south=…&east=…&west=…` URL filters by bounds, a bare
 * `/listings/rent` does not.
 */
export function parseBoundsFromParams(params: URLSearchParams): MapBounds | undefined {
  const read = (key: string): number | undefined => {
    const raw = params.get(key);
    if (raw === null) return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  };
  const north = read(LISTINGS_BOUNDS_PARAM_NAMES.north);
  const south = read(LISTINGS_BOUNDS_PARAM_NAMES.south);
  const east = read(LISTINGS_BOUNDS_PARAM_NAMES.east);
  const west = read(LISTINGS_BOUNDS_PARAM_NAMES.west);
  if (north === undefined || south === undefined || east === undefined || west === undefined) {
    return undefined;
  }
  return { north, south, east, west };
}
